import { getStorage } from 'minimal-shared/utils';
import { useLocalStorage } from 'minimal-shared/hooks';
import { useState, useEffect, createContext } from 'react';

import { MODELS } from 'src/utils/constants';

interface ISettingsContext {
  settings: {
    model: string;
    maxTokens: number;
    temperature: number;
    topP: number;
    topK: number;
    knowledgeBase: string[];
    knowledgeDocs?: string[];
    knowledgeBaseType?: string;
  };
  setSettings: React.Dispatch<React.SetStateAction<ISettingsContext['settings']>>;
  getModelDetails: (modelId: string) => any;
  getCurrentModel: () => any;
}

export const SettingsContext = createContext<ISettingsContext | undefined>(undefined);

const STORAGE_KEY = 'halo-ai-studio-run-settings';
const DEFAULT_MODEL = 'gpt-4o';

export function getModelDetails(modelId: string) {
  const models = MODELS.models as any;
  for (const provider in MODELS.models) {
    for (const model of models[provider]) {
      if (model.id === modelId) {
        return model;
      }
    }
  }
  return null;
}

export const DEFAULT_SETTINGS = {
  model: DEFAULT_MODEL,
  maxTokens: getModelDetails(DEFAULT_MODEL).token_limit || 4192,
  temperature: 0.1,
  topP: 0.9,
  topK: 40,
  knowledgeBase: [],
  knowledgeDocs: [],
  knowledgeBaseType: '',
};

const getStoredSettings = () => {
  try {
    const stored = getStorage<ISettingsContext['settings']>(STORAGE_KEY);
    if (stored && getModelDetails(stored.model)) return stored;
  } catch (error) {
    console.error('Error reading settings from localStorage:', error);
  }
  return DEFAULT_SETTINGS;
};

export const SettingsProvider = ({ children }: { children: React.ReactNode }) => {
  const [settings, setSettings] = useState<ISettingsContext['settings']>(getStoredSettings());
  const { setState } = useLocalStorage<ISettingsContext['settings']>(STORAGE_KEY, DEFAULT_SETTINGS);
  const getCurrentModel = () => getModelDetails(settings.model);
  useEffect(() => {
    try {
      setState(settings);
    } catch (error) {
      console.error('Error saving settings to localStorage:', error);
    }
  }, [settings, setState]);

  return (
    <SettingsContext.Provider value={{ settings, setSettings, getModelDetails, getCurrentModel }}>
      {children}
    </SettingsContext.Provider>
  );
};
